/**
 * LayerRegistry
 *
 * Singleton registry for managing all layer instances.
 * Provides a central place to create, retrieve, and manage layers.
 *
 * Usage:
 * ```typescript
 * const registry = LayerRegistry.getInstance(map);
 * const layer = await registry.createLayer(layerConfig);
 * registry.add(layer);
 * const retrieved = registry.get(layer.getId());
 * await registry.remove(layer.getId());
 * ```
 */

import mapboxgl from 'mapbox-gl';
import { BaseLayer, LayerConfig } from './BaseLayer';
import { WMSLayer } from './WMSLayer';
import { WFSLayer } from './WFSLayer';
import { TMSLayer } from './TMSLayer';
import { LiveGeoJSONLayer } from './LiveGeoJSONLayer';
import { LiveLayer } from './LiveLayer';
import { MapboxStyleLayer } from './MapboxStyleLayer';
import { VARLayer } from './VARLayer';
import { LayerProvider } from '../liveLayers/interfaces';
import { logger } from '../../utils/logger';

const log = logger('LayerRegistry');

/**
 * Layer factory function type
 */
type LayerFactory = (map: mapboxgl.Map, config: LayerConfig, provider?: LayerProvider) => BaseLayer;

/**
 * LayerRegistry singleton class
 */
export class LayerRegistry {
  /**
   * Singleton instance
   */
  private static instance: LayerRegistry | null = null;

  /**
   * Mapbox GL map instance
   */
  private map: mapboxgl.Map;

  /**
   * Map of layer ID to layer instance
   */
  private layers: Map<string, BaseLayer>;

  /**
   * Map of layer type to factory function
   */
  private factories: Map<string, LayerFactory>;

  /**
   * Map of provider ID to provider instance (for live layers)
   */
  private providers: Map<string, LayerProvider>;

  /**
   * Private constructor (singleton pattern)
   *
   * @param map Mapbox GL map instance
   */
  private constructor(map: mapboxgl.Map) {
    this.map = map;
    this.layers = new Map();
    this.providers = new Map();
    this.factories = new Map();

    // Register default layer factories
    this.registerFactory('wms', (map, config) => new WMSLayer(map, config));
    this.registerFactory('wfs', (map, config) => new WFSLayer(map, config));
    this.registerFactory('tms', (map, config) => new TMSLayer(map, config));
    this.registerFactory('geojson', (map, config) => new LiveGeoJSONLayer(map, config));
    this.registerFactory('mapbox-style', (map, config) => new MapboxStyleLayer(map, config));
    this.registerFactory('var', (map, config) => new VARLayer(map, config));
    this.registerFactory('live', (map, config, provider) => {
      if (!provider) {
        throw new Error('Live layer requires a LayerProvider instance');
      }
      return new LiveLayer(map, config, provider);
    });
  }

  /**
   * Get singleton instance
   *
   * @param map Mapbox GL map instance (required on first call)
   * @returns LayerRegistry instance
   */
  public static getInstance(map?: mapboxgl.Map): LayerRegistry {
    if (!LayerRegistry.instance) {
      if (!map) {
        throw new Error('Map instance required to initialize LayerRegistry');
      }
      LayerRegistry.instance = new LayerRegistry(map);
    } else if (map && LayerRegistry.instance.map !== map) {
      // Map instance changed (e.g., React StrictMode remount)
      // Update the registry to use the new map and clear old layers
      log.debug('Map instance changed - updating registry');
      LayerRegistry.instance.map = map;
      // Clear old layers since they belong to the old map
      LayerRegistry.instance.layers.clear();
    }
    return LayerRegistry.instance;
  }

  /**
   * Reset singleton instance (useful for testing)
   */
  public static resetInstance(): void {
    LayerRegistry.instance = null;
  }

  /**
   * Register a layer provider (for live layers)
   *
   * @param providerId Provider ID
   * @param provider Provider instance
   */
  public registerProvider(providerId: string, provider: LayerProvider): void {
    this.providers.set(providerId, provider);
  }

  /**
   * Get registered provider
   *
   * @param providerId Provider ID
   * @returns Provider instance or undefined
   */
  public getProvider(providerId: string): LayerProvider | undefined {
    return this.providers.get(providerId);
  }

  /**
   * Register a custom layer factory
   *
   * @param type Layer type
   * @param factory Factory function
   */
  public registerFactory(type: string, factory: LayerFactory): void {
    this.factories.set(type, factory);
  }

  /**
   * Create a layer from configuration
   *
   * @param config Layer configuration
   * @param provider Optional provider instance (for live layers)
   * @returns Layer instance
   */
  public createLayer(config: LayerConfig, provider?: LayerProvider): BaseLayer {
    const factory = this.factories.get(config.type);

    if (!factory) {
      throw new Error(`No factory registered for layer type: ${config.type}`);
    }

    // For live layers, get provider from registry if not provided
    if (config.type === 'live' && !provider) {
      const providerId = config.config.providerId;
      if (providerId) {
        provider = this.providers.get(providerId);
      }
    }

    return factory(this.map, config, provider);
  }

  /**
   * Add a layer to the registry
   *
   * @param layer Layer instance
   */
  public add(layer: BaseLayer): void {
    const id = layer.getId();

    if (this.layers.has(id)) {
      log.warn(`Layer with ID ${id} already exists in registry`);
      return;
    }

    this.layers.set(id, layer);
  }

  /**
   * Get a layer by ID
   *
   * @param id Layer ID
   * @returns Layer instance or undefined
   */
  public get(id: string): BaseLayer | undefined {
    return this.layers.get(id);
  }

  /**
   * Check if a layer exists in the registry
   *
   * @param id Layer ID
   * @returns True if layer exists
   */
  public has(id: string): boolean {
    return this.layers.has(id);
  }

  /**
   * Remove a layer from the registry and destroy it
   *
   * @param id Layer ID
   * @returns Promise that resolves when layer is removed
   */
  public async remove(id: string): Promise<void> {
    const layer = this.layers.get(id);

    if (!layer) {
      log.warn(`Layer with ID ${id} not found in registry`);
      return;
    }

    // Destroy the layer
    await layer.destroy();

    // Remove from registry
    this.layers.delete(id);
  }

  /**
   * Get all layers in the registry
   *
   * @returns Array of all layer instances
   */
  public getAll(): BaseLayer[] {
    return Array.from(this.layers.values());
  }

  /**
   * Get all layer IDs
   *
   * @returns Array of all layer IDs
   */
  public getAllIds(): string[] {
    return Array.from(this.layers.keys());
  }

  /**
   * Get layers by category
   *
   * @param category Layer category
   * @returns Array of layers in the category
   */
  public getByCategory(category: string): BaseLayer[] {
    return this.getAll().filter(layer => layer.getConfig().category === category);
  }

  /**
   * Get layers by type
   *
   * @param type Layer type
   * @returns Array of layers of the type
   */
  public getByType(type: string): BaseLayer[] {
    return this.getAll().filter(layer => layer.getConfig().type === type);
  }

  /**
   * Get layers by group
   *
   * @param group Layer group
   * @returns Array of layers in the group
   */
  public getByGroup(group: string): BaseLayer[] {
    return this.getAll().filter(layer => layer.getConfig().layer_group === group);
  }

  /**
   * Get enabled layers
   *
   * @returns Array of enabled layers
   */
  public getEnabled(): BaseLayer[] {
    return this.getAll().filter(layer => layer.isEnabled());
  }

  /**
   * Get disabled layers
   *
   * @returns Array of disabled layers
   */
  public getDisabled(): BaseLayer[] {
    return this.getAll().filter(layer => !layer.isEnabled());
  }

  /**
   * Enable a layer by ID
   *
   * @param id Layer ID
   * @returns Promise that resolves when layer is enabled
   */
  public async enableLayer(id: string): Promise<void> {
    const layer = this.layers.get(id);

    if (!layer) {
      throw new Error(`Layer with ID ${id} not found`);
    }

    await layer.enable();
  }

  /**
   * Disable a layer by ID
   *
   * @param id Layer ID
   * @returns Promise that resolves when layer is disabled
   */
  public async disableLayer(id: string): Promise<void> {
    const layer = this.layers.get(id);

    if (!layer) {
      throw new Error(`Layer with ID ${id} not found`);
    }

    await layer.disable();
  }

  /**
   * Toggle a layer by ID
   *
   * @param id Layer ID
   * @returns Promise that resolves when layer is toggled
   */
  public async toggleLayer(id: string): Promise<void> {
    const layer = this.layers.get(id);

    if (!layer) {
      throw new Error(`Layer with ID ${id} not found`);
    }

    if (layer.isEnabled()) {
      await layer.disable();
    } else {
      await layer.enable();
    }
  }

  /**
   * Set layer opacity
   *
   * @param id Layer ID
   * @param opacity Opacity value (0-1)
   * @returns Promise that resolves when opacity is set
   */
  public async setLayerOpacity(id: string, opacity: number): Promise<void> {
    const layer = this.layers.get(id);

    if (!layer) {
      throw new Error(`Layer with ID ${id} not found`);
    }

    await layer.setOpacity(opacity);
  }

  /**
   * Set layer order
   *
   * @param id Layer ID
   * @param order Order value (higher = on top)
   */
  public setLayerOrder(id: string, order: number): void {
    const layer = this.layers.get(id);

    if (!layer) {
      throw new Error(`Layer with ID ${id} not found`);
    }

    layer.setOrder(order);
  }

  /**
   * Reorder all layers based on their order values
   *
   * This ensures all layers are stacked correctly according to their order.
   * Layers are sorted by category first (backgrounds < overlays < live),
   * then by order within the same category.
   */
  public reorderAllLayers(): void {
    // Category priority mapping (lower number = lower in stack)
    const categoryPriority: Record<string, number> = {
      backgrounds: 0,
      overlays: 1,
      live: 2
    };

    // Only work with enabled layers — disabled layers don't participate in stacking
    const enabledLayers = this.getAll()
      .filter(l => l.isEnabled())
      .sort((a, b) => {
        const configA = a.getConfig();
        const configB = b.getConfig();

        // Primary sort: category (backgrounds always below overlays/live)
        const categoryDiff = categoryPriority[configA.category] - categoryPriority[configB.category];
        if (categoryDiff !== 0) {
          return categoryDiff;
        }

        // Secondary sort: order within same category
        // Tertiary sort: layer ID for stable ordering when order is equal
        const orderDiff = a.getOrder() - b.getOrder();
        if (orderDiff !== 0) {
          return orderDiff;
        }
        return a.getId().localeCompare(b.getId());
      });

    // Iterate bottom-to-top and move each Mapbox layer to the absolute top.
    // Last-call-wins gives the right internal order (top-most EM3 layer ends
    // up on top, lower-priority EM3 layers slide just beneath it), and every
    // EM3 layer ends up above all basemap layers (roads, labels, parks).
    // Users can adjust overlay opacity to see through; basemap layers have
    // no opacity control.
    enabledLayers.forEach(layer => {
      const mapboxLayerIds = layer.getMapboxLayerIds();
      mapboxLayerIds.forEach(layerId => {
        if (!this.map.getLayer(layerId)) return;
        try {
          this.map.moveLayer(layerId);
        } catch (error) {
          log.warn(`Failed to move layer ${layerId}:`, error);
        }
      });
    });
  }

  /**
   * Clear all layers from the registry
   *
   * @returns Promise that resolves when all layers are removed
   */
  public async clear(): Promise<void> {
    const promises = Array.from(this.layers.keys()).map(id => this.remove(id));
    await Promise.all(promises);
  }

  /**
   * Get registry statistics
   *
   * @returns Statistics object
   */
  public getStats(): {
    total: number;
    enabled: number;
    disabled: number;
    byType: Record<string, number>;
    byCategory: Record<string, number>;
  } {
    const layers = this.getAll();

    const stats = {
      total: layers.length,
      enabled: this.getEnabled().length,
      disabled: this.getDisabled().length,
      byType: {} as Record<string, number>,
      byCategory: {} as Record<string, number>
    };

    layers.forEach(layer => {
      const config = layer.getConfig();

      // Count by type
      stats.byType[config.type] = (stats.byType[config.type] || 0) + 1;

      // Count by category
      stats.byCategory[config.category] = (stats.byCategory[config.category] || 0) + 1;
    });

    return stats;
  }
}
